/** Scheda fornitore: anagrafica dal VendorHub (sola lettura), red flag, NC e claim insieme; NC da audit in arrivo. */
import { Card, Col, Row, Table } from "antd";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { FornitoreDto } from "@qa/shared";
import { api } from "../../api/client";
import { fmtImporto, fmtNum } from "../../lib/format";
import { Kpi, KpiRow, Miss, PageHead, Ro, Sev, Stato, StatoClaimTag, StatoRf, TipoNcTag, Vuoto } from "../../components/ui";

export default function FornitorePage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["fornitore", id], queryFn: () => api.get<FornitoreDto>(`/fornitori/${id}`) });
  return (
    <Stato q={q} righe={10}>
      {(f) => (
        <>
          <PageHead
            crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoClaim"), to: "/claim" }, { label: t("menu.fornitori"), to: "/fornitori" }, { label: f.nome }]}
            title={f.nome}
            sub={t("claim.fornitore.sottotitolo")}
            extra={<Ro>{t("claim.fornitore.anagraficaRo")}</Ro>}
          />
          <KpiRow>
            <Kpi label={t("claim.fornitore.rating")} value={f.rating ?? "—"} note={t("claim.fornitore.ratingNota")} />
            <Kpi label={t("claim.fornitore.redflag")} value={fmtNum(f.segnalazioni.length)} />
            <Kpi label={t("claim.fornitore.ncRf")} value={fmtNum(f.nc.length)} />
            <Kpi label={t("claim.fornitore.ncAudit")} value={f.ncAudit === null ? "—" : fmtNum(f.ncAudit)} note={t("claim.fornitore.ncAuditNota")} />
            <Kpi sensibile label={t("claim.fornitore.claim")} value={fmtNum(f.claim.length)} note={f.claim.map((c) => fmtImporto(c.importo, c.valuta)).join(" · ") || undefined} />
          </KpiRow>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card size="small" title={t("claim.fornitore.rfNc")}>
                {f.segnalazioni.length || f.nc.length ? (
                  <>
                    <Table
                      size="small"
                      rowKey="id"
                      showHeader={false}
                      pagination={false}
                      dataSource={f.segnalazioni}
                      columns={[
                        { key: "c", width: 90, render: (_, r) => <Link className="mono" to={`/redflag/segnalazioni/${r.id}`}>{r.codice}</Link> },
                        { key: "cl", render: (_, r) => r.cliente },
                        { key: "s", width: 90, render: (_, r) => <Sev s={r.severita} /> },
                        { key: "st", width: 120, render: (_, r) => <StatoRf s={r.stato} /> },
                      ]}
                    />
                    <Table
                      size="small"
                      rowKey="id"
                      showHeader={false}
                      pagination={false}
                      dataSource={f.nc}
                      style={{ marginTop: 8 }}
                      columns={[
                        { key: "c", width: 120, render: (_, n) => <Link className="mono" to={`/redflag/nc/${n.id}`}>{n.codice}</Link> },
                        { key: "t", render: (_, n) => <TipoNcTag tipo={n.tipo} /> },
                        { key: "f", width: 80, render: (_, n) => <span className="mono">{n.stato === "chiusa" ? t("nc.chiusa") : n.fase}</span> },
                      ]}
                    />
                  </>
                ) : (
                  <Vuoto titolo={t("comune.nessuno")} />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card size="small" title={t("claim.fornitore.claim")}>
                {f.claim.length ? (
                  <Table
                    size="small"
                    rowKey="id"
                    showHeader={false}
                    pagination={false}
                    dataSource={f.claim}
                    columns={[
                      { key: "c", width: 70, render: (_, c) => <Link className="mono" to={`/claim/${c.id}`}>{c.codice}</Link> },
                      { key: "b", render: (_, c) => c.brand },
                      { key: "p", render: (_, c) => c.problema },
                      { key: "s", render: (_, c) => <StatoClaimTag s={c.stato} /> },
                    ]}
                  />
                ) : (
                  <Vuoto titolo={t("comune.nessuno")} />
                )}
              </Card>
              <Card size="small" title={t("claim.fornitore.anagrafica")} style={{ marginTop: 16 }}>
                <dl className="kv">
                  <dt>{t("claim.fornitore.codice")}</dt>
                  <dd className="mono">{f.codice ?? "—"}</dd>
                  <dt>{t("claim.fornitore.nomeLocale")}</dt>
                  <dd>{f.nomeLocale ?? "—"}</dd>
                  <dt>{t("claim.fornitore.partitaIva")}</dt>
                  <dd className="mono">{f.partitaIva ?? "—"}</dd>
                  <dt>{t("claim.fornitore.indirizzo")}</dt>
                  <dd>{f.indirizzo ?? "—"}</dd>
                  <dt>{t("claim.fornitore.paese")}</dt>
                  <dd>{f.paese ?? "—"}</dd>
                  <dt>{t("claim.fornitore.statoVh")}</dt>
                  <dd>{f.statoVendorhub ?? <Miss>—</Miss>}</dd>
                  <dt>{t("claim.fornitore.stabilimenti")}</dt>
                  <dd>
                    {f.stabilimenti.length
                      ? f.stabilimenti.map((s) => (
                          <div key={s.id}>
                            {s.nome}
                            {s.codice ? <span className="mono muted"> · {s.codice}</span> : null}
                            {s.citta || s.paese ? <span className="muted"> · {[s.citta, s.paese].filter(Boolean).join(", ")}</span> : null}
                          </div>
                        ))
                      : "—"}
                  </dd>
                </dl>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Stato>
  );
}
